import orderBy from "lodash-es/orderBy";
import * as React from "react";
import { DiarySession, QuestionnaireTestAnswer, Visit } from "webportalclient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import { Link, useParams } from "react-router-dom";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";

export interface QuestionnaireResultsProps {
    visits: Visit[];
    sessions: DiarySession[];
}

export interface QuestionnaireResultProps {
    visit: Visit;
    session: DiarySession;
}

const DisplayQuestionnaireScoreSetting = "Vitalograph.WebPortal.Data.Settings.EDiary.DisplayQuestionnaireScoreSetting";
export const QuestionnaireResult = ({ visit, session }: QuestionnaireResultProps) => {
    const questionnaire = (answers: QuestionnaireTestAnswer[], name: string, score: number, symptomScore: undefined | number, activityLimitationScore: undefined | number, emotionalFunctionScore: undefined | number, environmentalScore: undefined |number) => {
        const sortedAnswers = orderBy(answers, a => parseInt(a.questionIndex));
        const [DisplayQuestionnaireScoreSettingResult, setDisplayQuestionnaireScoreSettingResult] = React.useState(false);
        const { study } = useCurrentStudyConfigurationContext();
        const client = useWebPortalApiClient();
        const legacyClient = useWebPortalLegacyClient();


        React.useEffect(() => {
            const getSettings = async () => {
                const settingsQuery = await legacyClient.getSettingsHelper(DisplayQuestionnaireScoreSetting, study.id);
                setDisplayQuestionnaireScoreSettingResult(settingsQuery.Data.length > 0 ? settingsQuery.Data[0].Value === "True" : false)
            }
            getSettings();
        }, []);

        return (
            <>
            <table className="table table-striped table-hover" data-automationid="QuestionnaireTable">
                <thead>
                    <tr>
                        <th>Question</th>
                        <th>Answer</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedAnswers.map((a, i) => {
                        return (
                            <tr key={`Question${a.questionIndex}`}>
                                <th>{a.questionText !== null ? <div>  Q{i + 1} - {a.questionText} </div> : <div> Q{ i + 1 } </div>}</th>
                                <td>{a.answerWeight !== null ? <div>  {a.answerWeight} - {a.answerText} </div> : a.answerText}</td>
                            </tr>);
                    })}
                </tbody>     
                </table>
                <div className="row">
                    <div className="col-md-12">
                        {score !== undefined && score !== null && score !== -1 && DisplayQuestionnaireScoreSettingResult !== false ? <>Score: <b data-automationid="QuestionnaireScore">{score.toFixed(2)}</b>{" "}</> : null}
                        {symptomScore ? <>Symptom Score: <b data-automationid="SymptomScore">{symptomScore.toFixed(2)}</b>{" "}</> : null}
                        {activityLimitationScore ? <>Activity Limitation Score: <b data-automationid="ActivityLimitationScore">{activityLimitationScore.toFixed(2)}</b>{" "}</> : null}
                        {emotionalFunctionScore ? <>Emotional Function Score: <b data-automationid="EmotionalFunctionScore">{emotionalFunctionScore.toFixed(2)}</b>{" "}</> : null}
                        {environmentalScore ? <>Environmental Stimuli Score: <b data-automationid="EnvironmentalScore">{environmentalScore.toFixed(2)}</b>{" "}</> : null}
                    </div>
                </div>
            </>
        );
    }

    if (session.questionnaires) {
        return (
            <>
                {session.questionnaires.map(q => (
                    <div key={`Questionnaire${q.id}`} data-automationid="QuestionnaireWrapper">
                        <h4 className="text-primary" data-automationid="QuestionnaireNameHeader">
                            Visit {visit.name} Session {session.name} {q.name} performed on <LocalizedDateTime time={q.occurred} accuracy={DateTimeAccuracy.Second} isTooltip={true} />
                        </h4>
                        {questionnaire(q.answers, q.name, q.score, q.symptomScore, q.activityLimitationScore, q.emotionalFunctionScore, q.environmentalScore)}
                    </div>
                ))}
            </>
        );
    }
}

export const QuestionnaireResults = ({ visits, sessions }: QuestionnaireResultsProps) => {
    if (sessions.map(s => s.questionnaires).flat().length === 0) {
        return <Alert show={true} category={AlertCategoryEnum.Info}>
            There are no questionnaire results for this date
            </Alert>;
    }

    return (
        <>
            {sessions.map(s => (<QuestionnaireResult key={`QuestionnaireResult${s.id}`} visit={visits.find(v => v.id === s.visitId)} session={s} />))}
        </>
    );
    
}
